// ----------------------------------------------------------------------
//
// Which workflow steps each off-campus program uses (#625). Work Experience,
// Career Internship and RAP share one workflow: learning plan survey, the
// Workplace Practicum Agreement, hours in 125-hour segments, and the employer,
// self and coordinator evaluations. Green Certificate is different: it has no
// agreement in the system (the coordinator sets the placement active by
// hand), the learning plan is the competencies in each GC manual, there are no
// hours, and each course is marked by the regional tester. A GC placement is
// open-ended, so it needs no end date.
//
// Code that depends on a step asks programWorkflow(programType), never the
// program code itself. A placement or referral with no program yet gets the
// shared workflow.
//
// Kept identical in server/services/off-campus/program-workflow.js and
// src/sections/off-campus/program-workflow.js (the CRA app can't import from
// outside src/); server/constants.sync.test.js fails if they drift.
//
// ----------------------------------------------------------------------

// What each program is called wherever people read it. Never show the code
// itself: students, parents and employers don't know what "GC" or "RAP" mean.
const PROGRAM_NAMES = Object.freeze({
  WE: 'Work Experience',
  CI: 'Career Internship',
  RAP: 'Registered Apprenticeship Program',
  GC: 'Green Certificate',
});

/** A program's full name; `fallback` for no program or an unknown code. */
function programName(programType, fallback = '') {
  return PROGRAM_NAMES[programType] || fallback;
}

// A program with no agreement has `agreement: null`: nothing blocks it from going active
const AGREEMENTS = { wpa: 'wpa' };
const EVALUATIONS = { threePart: 'three_part', testerMarks: 'tester_marks' };
const ONBOARDING_TEMPLATES = { general: 'general', greenCertificate: 'greenCertificate' };

const SHARED_WORKFLOW = Object.freeze({
  agreement: AGREEMENTS.wpa,
  learningPlanSurvey: true,
  hours: true,
  segments: true,
  evaluation: EVALUATIONS.threePart,
  endDateRequired: true,
  onboardingTemplate: ONBOARDING_TEMPLATES.general,
});

const PROGRAM_WORKFLOWS = Object.freeze({
  WE: SHARED_WORKFLOW,
  CI: SHARED_WORKFLOW,
  RAP: SHARED_WORKFLOW,
  GC: Object.freeze({
    agreement: null,
    learningPlanSurvey: false,
    hours: false,
    segments: false,
    evaluation: EVALUATIONS.testerMarks,
    endDateRequired: false,
    onboardingTemplate: ONBOARDING_TEMPLATES.greenCertificate,
  }),
});

/** The workflow for a program code; the shared workflow when there is none (or it's unknown). */
function programWorkflow(programType) {
  return PROGRAM_WORKFLOWS[programType] || SHARED_WORKFLOW;
}

module.exports = {
  AGREEMENTS,
  EVALUATIONS,
  ONBOARDING_TEMPLATES,
  PROGRAM_NAMES,
  PROGRAM_WORKFLOWS,
  programName,
  programWorkflow,
};
